import React, { useMemo, useState } from 'react';
import {
  Stack, Group, Title, Text, SegmentedControl, Paper, SimpleGrid,
  Loader, Drawer, Table, ScrollArea, Badge, Anchor,
} from '@mantine/core';
import { Sankey, Tooltip, Rectangle, Layer, ResponsiveContainer } from 'recharts';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import DraftBadge from '../../common/DraftBadge';

// Disposition colors (match the funnel semantics; Mantine-hued to fit house style).
const DISP_COLORS = {
  rfq_with_parts: '#2f9e44', // green
  not_rfq:        '#868e96', // gray
  parse_failed:   '#e03131', // red
  pending:        '#1971c2', // blue
};
const DISP_BADGE = { rfq_with_parts: 'green', not_rfq: 'gray', parse_failed: 'red', pending: 'blue' };
const GROUP_COLOR = '#7048e8'; // indigo for the source (company/segment) nodes

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function trunc(s, n) { return s && s.length > n ? s.slice(0, n - 1) + '…' : s; }

function KpiCard({ label, value, color }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Title order={2} c={color}>{value}</Title>
      <Text size="sm" c="dimmed">{label}</Text>
    </Paper>
  );
}

// Custom Sankey node: colored rect + outside label (groups left, dispositions right).
function SankeyNode(props) {
  const { x, y, width, height, payload } = props;
  const isGroup = payload.kind === 'group';
  const fill = isGroup ? GROUP_COLOR : (DISP_COLORS[payload.key] || '#868e96');
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={fill} fillOpacity={0.9} />
      <text
        x={isGroup ? x - 6 : x + width + 6}
        y={y + height / 2}
        textAnchor={isGroup ? 'end' : 'start'}
        dominantBaseline="central"
        fontSize={11}
        fill="var(--mantine-color-text)"
      >
        {trunc(payload.name, isGroup ? 22 : 30)}
      </text>
    </Layer>
  );
}

// Custom Sankey link: bezier stroked in the target disposition's color.
function SankeyLinkEl(props) {
  const { sourceX, sourceY, sourceControlX, targetControlX, targetX, targetY, linkWidth, payload, nodes } = props;
  const ti = typeof payload.target === 'number' ? payload.target : payload.target?.index;
  const key = nodes?.[ti]?.key;
  const color = DISP_COLORS[key] || '#adb5bd';
  return (
    <path
      d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
      fill="none"
      stroke={color}
      strokeOpacity={0.3}
      strokeWidth={Math.max(1, linkWidth)}
    />
  );
}

export default function InboundEmails() {
  const [groupBy, setGroupBy] = useState('company');
  const [days, setDays] = useState('3');
  const [drill, setDrill] = useState(null); // selected group name for the drawer

  const flowUrl = useMemo(() => {
    const p = new URLSearchParams();
    p.set('groupBy', groupBy);
    p.set('days', days);
    p.set('topN', '12');
    return `${config.apiBaseUrl}/api/funnel/flow?${p.toString()}`;
  }, [groupBy, days]);
  const { data, isLoading, error } = useAuthenticatedData(flowUrl);

  const tiles = data?.tiles;
  const sankey = data?.sankey;
  const groups = data?.groups ?? [];
  const nodes = sankey?.nodes ?? [];
  const hasSankey = sankey && Array.isArray(sankey.links) && sankey.links.length > 0;

  const drillUrl = useMemo(() => {
    if (!drill) return null;
    const p = new URLSearchParams();
    p.set('groupBy', groupBy);
    p.set('group', drill);
    p.set('days', days);
    p.set('take', '300');
    return `${config.apiBaseUrl}/api/funnel/emails?${p.toString()}`;
  }, [drill, groupBy, days]);
  const { data: drillRows } = useAuthenticatedData(drillUrl);

  const columns = useMemo(() => [
    { accessorKey: 'group', header: groupBy === 'segment' ? 'Segment' : 'Company' },
    { accessorKey: 'received', header: 'Received' },
    { accessorKey: 'rfqWithParts', header: 'RFQ w/ parts' },
    { accessorKey: 'notRfq', header: 'Not an RFQ' },
    { accessorKey: 'parseFailed', header: 'Parse failed' },
    { accessorKey: 'pending', header: 'Pending' },
  ], [groupBy]);

  const table = useMantineReactTable({
    columns,
    data: groups,
    enableRowSelection: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enablePagination: groups.length > 15,
    initialState: { density: 'xs' },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => setDrill(row.original.group),
      style: { cursor: 'pointer' },
    }),
  });

  const segmentUnresolved = groupBy === 'segment' && groups.length > 0
    && groups.every((g) => g.group === '(unresolved)');

  return (
    <Stack gap="md" p="md">
      <DraftBadge />
      <Group justify="space-between" align="center">
        <Title order={2}>Inbound Emails</Title>
        <Group gap="sm">
          <SegmentedControl size="xs" value={groupBy} onChange={setGroupBy}
            data={[{ label: 'Company', value: 'company' }, { label: 'Segment', value: 'segment' }]} />
          <SegmentedControl size="xs" value={days} onChange={setDays}
            data={[{ label: '3d', value: '3' }, { label: '7d', value: '7' }, { label: '14d', value: '14' }, { label: '30d', value: '30' }]} />
        </Group>
      </Group>

      {isLoading && <Stack align="center" mt="xl"><Loader /><Text c="dimmed">Loading funnel…</Text></Stack>}
      {error && <Text c="red">Failed to load funnel data. Is the API reachable?</Text>}

      {!isLoading && !error && tiles && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }}>
            <KpiCard label="Total emails" value={tiles.total} />
            <KpiCard label="RFQ with parts" value={`${tiles.rfqWithParts} (${tiles.rfqPct}%)`} color="green" />
            <KpiCard label="Not an RFQ" value={tiles.notRfq} />
            <KpiCard label="AI parse failed" value={tiles.parseFailed} color={tiles.parseFailed > 0 ? 'red' : undefined} />
            <KpiCard label="Pending" value={tiles.pending} />
          </SimpleGrid>

          {segmentUnresolved && (
            <Text size="sm" c="dimmed">
              Segment is unresolved until the customer map is seeded (POST <code>/api/funnel/seed-customer-map</code>).
              Switch to <b>Company</b> to see grouping now.
            </Text>
          )}

          <Paper withBorder radius="md" p="md">
            <Text fw={600} mb="xs">Email flow — where they came from → what we did</Text>
            {hasSankey ? (
              <ResponsiveContainer width="100%" height={Math.max(360, nodes.length * 22)}>
                <Sankey
                  data={sankey}
                  nodePadding={22}
                  nodeWidth={12}
                  margin={{ left: 140, right: 150, top: 12, bottom: 12 }}
                  node={<SankeyNode />}
                  link={<SankeyLinkEl nodes={nodes} />}
                >
                  <Tooltip />
                </Sankey>
              </ResponsiveContainer>
            ) : (
              <Text c="dimmed">No emails in this window.</Text>
            )}
          </Paper>

          <Paper withBorder radius="md">
            <MantineReactTable table={table} />
          </Paper>
        </>
      )}

      <Drawer opened={!!drill} onClose={() => setDrill(null)} position="right" size="xl"
        title={<Text fw={600}>Emails — {drill}</Text>}>
        <ScrollArea>
          <Table striped highlightOnHover fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Received</Table.Th>
                <Table.Th>From</Table.Th>
                <Table.Th>Subject</Table.Th>
                <Table.Th>Disposition</Table.Th>
                <Table.Th>Parts</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(drillRows ?? []).map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>{fmtDate(r.received_datetime)}</Table.Td>
                  <Table.Td><Text size="xs">{r.from_address}</Text></Table.Td>
                  <Table.Td>
                    {r.web_link
                      ? <Anchor href={r.web_link} target="_blank" size="sm">{trunc(r.subject, 70) || '(no subject)'}</Anchor>
                      : <Text size="sm">{trunc(r.subject, 70) || '(no subject)'}</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={DISP_BADGE[r.disposition] || 'gray'} variant="light">
                      {r.disposition}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{r.part_count}</Table.Td>
                </Table.Tr>
              ))}
              {(!drillRows || drillRows.length === 0) && (
                <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" py="md">No emails.</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Drawer>
    </Stack>
  );
}
